import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { currentRouteSet } from "../../actions/actions";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import { API_URL } from "../../config";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import SearchIcon from "@material-ui/icons/Search";
import PrintIcon from "@material-ui/icons/Print";
import GetAppIcon from "@material-ui/icons/GetApp";
import InstitutionProfile from "../commons/institution_profile";
import { NetworkLoaderOverlay } from "../commons/NetworkLoader";
import ReactToPrint from "react-to-print";
import commaNumber from "comma-number";
import swal from "sweetalert";
import { exportToExcel, pathSpliter } from "../../lib/functions";
import { cacheSalesMasterData, loadSalesMasterData } from "../../lib/offlineSales";
import { getOfflineValue, setOfflineValue } from "../../lib/offlineIndexedDB";
import "../commons/commons.css";

const SEARCH_FIELD_ALIASES = {
  name: ["name", "product", "productname"],
  code: ["code", "sku", "productcode"],
  barcode: ["barcode", "bar", "bar_code"],
  brand: ["brand"],
  category: ["category", "cat"],
  type: ["type", "producttype"],
};

const ProductSearch = ({ location, currentRouteSet, authInfo }) => {
  const classes = useStyles();

  const [products, productsSet] = useState([]);
  const [selectedProduct, selectedProductSet] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [productLookupLoading, setProductLookupLoading] = useState(false);

  const [stockData, stockDataSet] = useState([]);
  const reportRef = useRef(null);
  const [print, printSet] = useState(false);
  const [reportLoading, reportLoadingSet] = useState(false);
  const selectedProductRef = useRef(null);
  const selectedFromDropdownRef = useRef(false);
  const searchInputRef = useRef("");
  const getSearchResultRef = useRef(null);

  // Put the ref on the TextField input (NOT on Autocomplete)
  const productInputRef = useRef(null);

  let format = commaNumber.bindWith(",", ".");
  const cleanProductName = (name) =>
    String(name || "")
      .replace(/^\s*\d+\s+/, "")
      .trim();
  const normalizeToken = (text) =>
    String(text || "")
      .trim()
      .toLowerCase()
      .replace(/[–—−]/g, " ")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  const getProductLabel = (option) => {
    const parts = [
      cleanProductName(option?.prod_name),
      String(option?.prod_brand_name || "").trim(),
      String(option?.prod_color_name || "").trim(),
    ].filter(Boolean);
    return parts.join(" - ");
  };
  const normalizeText = (text) => normalizeToken(text);
  const getProductBarcode = (product) =>
    String(
      product?.prod_barcode ??
      product?.barcode ??
      product?.bar_code ??
      product?.product_barcode ??
      ""
    ).trim();
  const getProductType = (product) =>
    String(
      product?.prod_type_name ??
      product?.prod_type ??
      product?.product_type ??
      product?.type ??
      (product?.prod_is_service === "true" || product?.prod_is_service === true ? "service" : "finished")
    ).trim();
  const pickFirstValue = (source, keys, fallback = "") => {
    for (const key of keys) {
      const value = source?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }
    return fallback;
  };
  const getProductCountry = (row) =>
    pickFirstValue(row, ["prod_color_name", "country_name", "country", "CountryName"], "");
  const getProductSearchParts = (product) => [
    cleanProductName(product?.prod_name),
    product?.prod_name,
    product?.prod_code,
    getProductBarcode(product),
    product?.prod_cat_name,
    product?.prod_brand_name,
    product?.prod_color_name,
    getProductType(product),
  ].filter(Boolean);
  const getProductSearchText = (product) =>
    normalizeText(getProductSearchParts(product).join(" "));
  const parseAdvancedQuery = (queryText) => {
    const raw = String(queryText || "").trim();
    const fieldMatches = [...raw.matchAll(/([a-zA-Z]+):([^\s]+)/g)];
    const fieldFilters = fieldMatches.reduce((acc, [, rawKey, rawValue]) => {
      const canonicalKey = Object.entries(SEARCH_FIELD_ALIASES).find(([, aliases]) =>
        aliases.includes(normalizeText(rawKey))
      )?.[0];
      if (canonicalKey) {
        acc[canonicalKey] = normalizeText(rawValue);
      }
      return acc;
    }, {});

    const freeText = raw.replace(/([a-zA-Z]+):([^\s]+)/g, " ").trim();
    const tokens = freeText
      .split(/\s+/)
      .map(normalizeText)
      .filter(Boolean);

    return { fieldFilters, tokens, raw: normalizeText(raw) };
  };
  const productMatchesQuery = (product, queryText) => {
    const { fieldFilters, tokens, raw } = parseAdvancedQuery(queryText);
    if (!raw) return true;

    const fieldValues = {
      name: normalizeText(cleanProductName(product?.prod_name) || product?.prod_name),
      code: normalizeText(product?.prod_code),
      barcode: normalizeText(getProductBarcode(product)),
      brand: normalizeText(product?.prod_brand_name),
      category: normalizeText(product?.prod_cat_name),
      type: normalizeText(getProductType(product)),
    };

    const fieldFilterOk = Object.entries(fieldFilters).every(([key, value]) =>
      fieldValues[key]?.includes(value)
    );
    if (!fieldFilterOk) return false;

    const haystack = getProductSearchText(product);
    if (tokens.length === 0) return true;
    return tokens.every((token) => haystack.includes(token));
  };
  const getResolvedProductId = (product) => {
    const rawId =
      product?.prod_id ??
      product?.product_id ??
      product?.id ??
      product?.prodId ??
      (typeof product?.value === "object"
        ? product?.value?.prod_id ?? product?.value?.product_id ?? product?.value?.id
        : product?.value);
    return rawId === undefined || rawId === null || rawId === "" ? null : rawId;
  };
  const getProductLocationCacheKey = (productId) => `sales_product_locations_${productId}`;
  const getProductStockCacheKey = (productId) => `sales_product_stock_${productId}`;
  const getProductLocationMergeKey = (row = {}) => {
    const purchaseRate = Number.parseFloat(row?.prod_purchase_rate ?? 0);
    const normalizedRate = Number.isFinite(purchaseRate) ? purchaseRate.toFixed(6) : "0.000000";
    const floorId = Number(row?.prod_floor_id ?? row?.floor_id ?? 0);
    const rackId = Number(row?.prod_rack_id ?? row?.rack_id ?? 0);
    const floorKey = floorId > 0 ? `floor:${floorId}` : `floor:${String(row?.prod_floor_name ?? "").trim().toLowerCase()}`;
    const rackKey = rackId > 0 ? `rack:${rackId}` : `rack:${String(row?.prod_rack_name ?? "").trim().toLowerCase()}`;
    return [row?.prod_id, floorKey, rackKey, normalizedRate].join("||");
  };
  const groupLocationRows = (rows) => {
    const grouped = new Map();

    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const purchaseRate = Number.parseFloat(row?.prod_purchase_rate ?? 0);
      const key = getProductLocationMergeKey(row);

      if (!grouped.has(key)) {
        grouped.set(key, {
          ...row,
          prod_floor_id: Number(row?.prod_floor_id ?? row?.floor_id ?? 0),
          prod_rack_id: Number(row?.prod_rack_id ?? row?.rack_id ?? 0),
          prod_purchase_rate: Number.isFinite(purchaseRate) ? Number(purchaseRate.toFixed(6)) : 0,
          prod_qty: parseFloat(row?.prod_qty ?? 0),
          __mergeKey: key,
        });
        return;
      }

      const existing = grouped.get(key);
      grouped.set(key, {
        ...existing,
        prod_purchase_rate: Number.isFinite(purchaseRate)
          ? Number(purchaseRate.toFixed(6))
          : Number(existing?.prod_purchase_rate ?? 0),
        prod_qty: parseFloat(existing?.prod_qty ?? 0) + parseFloat(row?.prod_qty ?? 0),
        __mergeKey: key,
      });
    });

    const groupedRows = Array.from(grouped.values());
    const weightedAverageByProduct = new Map();

    groupedRows.forEach((row) => {
      const productId = Number(row?.prod_id || 0);
      const qty = Number.parseFloat(row?.prod_qty || 0);
      const rate = Number.parseFloat(row?.prod_purchase_rate || 0);
      if (!productId) return;

      const current = weightedAverageByProduct.get(productId) || { qty: 0, value: 0 };
      current.qty += Number.isFinite(qty) ? qty : 0;
      current.value += (Number.isFinite(qty) ? qty : 0) * (Number.isFinite(rate) ? rate : 0);
      weightedAverageByProduct.set(productId, current);
    });

    return groupedRows
      .map((row) => {
        const productId = Number(row?.prod_id || 0);
        const aggregate = weightedAverageByProduct.get(productId);
        const averagePurchaseRate =
          aggregate && aggregate.qty > 0
            ? aggregate.value / aggregate.qty
            : Number(row?.average_purchase_rate ?? row?.prod_purchase_rate ?? 0);

        return {
          ...row,
          average_purchase_rate: averagePurchaseRate,
        };
      })
      .sort((left, right) => {
      const leftFloor = String(left?.prod_floor_name ?? "").localeCompare(String(right?.prod_floor_name ?? ""));
      if (leftFloor !== 0) return leftFloor;
      const leftRack = String(left?.prod_rack_name ?? "").localeCompare(String(right?.prod_rack_name ?? ""));
      if (leftRack !== 0) return leftRack;
      return Number(left?.prod_purchase_rate ?? 0) - Number(right?.prod_purchase_rate ?? 0);
      });
  };
  const rowMatchesQuery = (row, queryText) => {
    const haystack = normalizeText([
      row?.prod_name,
      row?.prod_code,
      row?.prod_brand_name,
      row?.prod_color_name,
      row?.prod_cat_name,
      row?.prod_floor_name,
      row?.prod_rack_name,
      row?.prod_barcode,
      row?.prod_type_name,
    ].filter(Boolean).join(" "));
    const { tokens, fieldFilters } = parseAdvancedQuery(queryText);
    const fieldValues = {
      name: normalizeText(row?.prod_name),
      code: normalizeText(row?.prod_code),
      barcode: normalizeText(getProductBarcode(row)),
      brand: normalizeText(row?.prod_brand_name),
      category: normalizeText(row?.prod_cat_name),
      type: normalizeText(getProductType(row)),
    };
    const fieldFilterOk = Object.entries(fieldFilters).every(([key, value]) =>
      fieldValues[key]?.includes(value)
    );
    if (!fieldFilterOk) return false;
    if (tokens.length === 0) return true;
    return tokens.every((token) => haystack.includes(token));
  };
  const enrichOfflineRow = async (row, product = null, floorNameMap = new Map()) => {
    const productId = getResolvedProductId(product || row);
    const cachedStock = productId != null ? await getOfflineValue(getProductStockCacheKey(productId), null) : null;
    const stockQty = cachedStock != null && cachedStock !== ""
      ? Number(cachedStock)
      : Number(pickFirstValue(row, ["prod_qty", "current_quantity", "current_stock", "stock_qty", "qty"], 0));
    const floorId = Number(pickFirstValue(row, ["prod_floor_id", "floor_id", "prod_floor"], product?.prod_floor_id ?? 0));
    const rackId = Number(pickFirstValue(row, ["prod_rack_id", "rack_id", "prod_rack"], product?.prod_rack_id ?? 0));
    const resolvedFloorName =
      pickFirstValue(row, ["prod_floor_name", "floor_name", "FloorName"]) ||
      pickFirstValue(product, ["prod_floor_name", "floor_name", "FloorName"]) ||
      floorNameMap.get(floorId) ||
      (floorId ? `Floor #${floorId}` : "");
    const resolvedRackName =
      pickFirstValue(row, ["prod_rack_name", "rack_name", "RackName"]) ||
      pickFirstValue(product, ["prod_rack_name", "rack_name", "RackName"]) ||
      (rackId ? `Rack #${rackId}` : "");
    return {
      ...row,
      prod_id: productId ?? row?.prod_id ?? null,
      prod_name: row?.prod_name ?? product?.prod_name ?? "",
      prod_code: row?.prod_code ?? product?.prod_code ?? "",
      prod_cat_name: row?.prod_cat_name ?? product?.prod_cat_name ?? "",
      prod_brand_name: row?.prod_brand_name ?? product?.prod_brand_name ?? "",
      prod_color_name: row?.prod_color_name ?? product?.prod_color_name ?? "",
      prod_floor_name: resolvedFloorName,
      prod_rack_name: resolvedRackName,
      prod_qty: Number.isFinite(stockQty) ? stockQty : 0,
      prod_unit_name: row?.prod_unit_name ?? product?.prod_unit_name ?? "",
      prod_purchase_rate: row?.prod_purchase_rate ?? product?.prod_purchase_rate ?? 0,
      average_purchase_rate: row?.average_purchase_rate ?? product?.average_purchase_rate ?? 0,
      last_sale_rate: row?.last_sale_rate ?? product?.last_sale_rate ?? 0,
      last_sale_date: row?.last_sale_date ?? product?.last_sale_date ?? "",
    };
  };
  const buildOfflineSearchRows = async (queryText, productOverride = null, forceTextSearch = false) => {
    const typedText = String(queryText || "").trim();
    const resolvedProduct = forceTextSearch
      ? null
      : resolveProductForSearch(productOverride || selectedProduct, typedText);
    const cachedFloors = await loadSalesMasterData("floors", []);
    const floorNameMap = new Map(
      (Array.isArray(cachedFloors) ? cachedFloors : []).map((floor) => [
        Number(floor?.prod_floor_id || 0),
        floor?.prod_floor_name || "",
      ])
    );
    let candidateProducts = [];

    if (resolvedProduct && getResolvedProductId(resolvedProduct) !== null) {
      candidateProducts = [resolvedProduct];
    } else if (typedText) {
      candidateProducts = products.filter((product) => productMatchesQuery(product, typedText));
    }

    if (candidateProducts.length === 0 && resolvedProduct) {
      candidateProducts = [resolvedProduct];
    }
    if (candidateProducts.length === 0 && typedText) {
      const matchedProduct = findProductByTypedText(typedText);
      if (matchedProduct) candidateProducts = [matchedProduct];
    }

    const rows = [];
    const cachedGlobalLocationRows = await loadSalesMasterData("product-locations", []);

    for (const product of candidateProducts.slice(0, 50)) {
      const productId = getResolvedProductId(product);
      if (productId == null) continue;

      const cachedRows = await getOfflineValue(getProductLocationCacheKey(productId), []);
      if (Array.isArray(cachedRows) && cachedRows.length > 0) {
        for (const row of cachedRows) {
          rows.push(await enrichOfflineRow(row, product, floorNameMap));
        }
        continue;
      }

      const matchingGlobalRows = Array.isArray(cachedGlobalLocationRows)
        ? cachedGlobalLocationRows.filter((row) =>
            Number(row?.prod_id || 0) === Number(productId) || rowMatchesQuery(row, typedText)
          )
        : [];

      if (matchingGlobalRows.length > 0) {
        for (const row of matchingGlobalRows) {
          rows.push(await enrichOfflineRow(row, product, floorNameMap));
        }
        continue;
      }

      rows.push(await enrichOfflineRow({
        prod_id: productId,
        prod_name: product?.prod_name ?? "",
        prod_code: product?.prod_code ?? "",
        prod_cat_name: product?.prod_cat_name ?? "",
        prod_brand_name: product?.prod_brand_name ?? "",
        prod_color_name: product?.prod_color_name ?? "",
        prod_floor_name: pickFirstValue(product, ["prod_floor_name", "floor_name", "FloorName"], ""),
        prod_rack_name: pickFirstValue(product, ["prod_rack_name", "rack_name", "RackName"], ""),
        prod_qty: 0,
        prod_unit_name: product?.prod_unit_name ?? "",
        prod_purchase_rate: product?.prod_purchase_rate ?? 0,
        average_purchase_rate: product?.average_purchase_rate ?? 0,
        last_sale_rate: product?.last_sale_rate ?? 0,
        last_sale_date: product?.last_sale_date ?? "",
      }, product, floorNameMap));
    }

    if (rows.length === 0 && typedText && Array.isArray(cachedGlobalLocationRows) && cachedGlobalLocationRows.length > 0) {
      const fallbackRows = cachedGlobalLocationRows.filter((row) => rowMatchesQuery(row, typedText));
      for (const row of fallbackRows) {
        rows.push(await enrichOfflineRow(row, null, floorNameMap));
      }
    }

    const normalized = groupLocationRows(rows);
    return normalized.filter((row) => rowMatchesQuery(row, typedText) || !typedText);
  };
  const getResolvedDisplayValue = (row, keys, fallback = "") =>
    pickFirstValue(row, keys, fallback);
  const findProductByTypedText = (text) => {
    const query = normalizeText(text);
    if (!query) return null;

    const exact = products.find((p) => normalizeText(getProductLabel(p)) === query);
    if (exact) return exact;

    const exactBySearchText = products.find((p) => getProductSearchText(p) === query);
    if (exactBySearchText) return exactBySearchText;

    const exactByCode = products.find((p) => normalizeText(p?.prod_code) === query);
    if (exactByCode) return exactByCode;

    const exactByBarcode = products.find((p) => normalizeText(getProductBarcode(p)) === query);
    if (exactByBarcode) return exactByBarcode;

    const exactByName = products.find((p) => normalizeText(cleanProductName(p?.prod_name)) === query);
    if (exactByName) return exactByName;

    return (
      products.find((p) => productMatchesQuery(p, text)) || null
    );
  };
  const resolveProductForSearch = (candidateProduct, typedText = "") => {
    if (candidateProduct) {
      if (getResolvedProductId(candidateProduct) !== null) return candidateProduct;
      const byCandidateLabel = findProductByTypedText(
        typeof candidateProduct === "string" ? candidateProduct : getProductLabel(candidateProduct)
      );
      if (byCandidateLabel) return byCandidateLabel;
    }
    return findProductByTypedText(typedText);
  };

  useEffect(() => {
    currentRouteSet(pathSpliter(location.pathname, 1));
    getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProducts = async () => {
    if (navigator.onLine === false || !authInfo?.token) {
      const cachedProducts = await loadSalesMasterData("products", []);
      productsSet(Array.isArray(cachedProducts) ? cachedProducts : []);
      return;
    }

    try {
      const res = await axios.post(
        `${API_URL}/api/get-products`,
        { "select-type": "active", limit: 80 },
        { headers: { "auth-token": authInfo.token } }
      );

      // Make sure it's always an array
      const nextValue = Array.isArray(res?.data?.message) ? res.data.message : [];
      productsSet(nextValue);
    } catch (err) {
      const cachedProducts = await loadSalesMasterData("products", []);
      productsSet(Array.isArray(cachedProducts) ? cachedProducts : []);
    }
  };

  const searchProductOptions = async (queryText) => {
    const searchText = String(queryText || "").trim();
    if (navigator.onLine === false || !authInfo?.token || searchText.length < 2) return;

    setProductLookupLoading(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/get-products`,
        { "select-type": "active", searchText, limit: 50 },
        { headers: { "auth-token": authInfo.token } }
      );
      const nextValue = Array.isArray(res?.data?.message) ? res.data.message : [];
      const merged = new Map();
      [...nextValue, ...products, selectedProduct].filter(Boolean).forEach((product) => {
        const productId = Number(product?.prod_id || 0);
        if (!productId || merged.has(productId)) return;
        merged.set(productId, product);
      });
      productsSet(Array.from(merged.values()));
    } catch (err) {
      console.error("Product option search failed:", err);
    } finally {
      setProductLookupLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!stockData || stockData.length === 0) return;

    if (e.key === "ArrowDown") {
      setSelectedRow((prev) =>
        prev === null || prev === stockData.length - 1 ? 0 : prev + 1
      );
    }
    if (e.key === "ArrowUp") {
      setSelectedRow((prev) =>
        prev === null || prev === 0 ? stockData.length - 1 : prev - 1
      );
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [stockData]);

  async function getSearchResult(productOverride = null, options = {}) {
    const typedText = searchInput || productInputRef.current?.value || "";
    const shouldUseSelectedProduct =
      Boolean(productOverride) ||
      (
        selectedFromDropdownRef.current &&
        selectedProduct &&
        normalizeText(typedText) === normalizeText(getProductLabel(selectedProduct))
      );
    const forceTextSearch = options.forceTextSearch || !shouldUseSelectedProduct;
    const productToSearch = forceTextSearch
      ? null
      : resolveProductForSearch(productOverride || selectedProduct, typedText);
    const resolvedProductId = getResolvedProductId(productToSearch);
    const normalizedTypedText = String(typedText || "").trim();

    if (resolvedProductId === null && !normalizedTypedText) {
      swal({ title: "Product not found", text: "Try name, code, barcode, or multiple keywords.", icon: "warning" });
      return;
    }
    if (productToSearch && productToSearch !== selectedProduct) {
      selectedProductSet(productToSearch);
    }

    if (navigator.onLine === false || !authInfo?.token) {
      reportLoadingSet(true);
      try {
        const offlineRows = await buildOfflineSearchRows(normalizedTypedText, productToSearch, forceTextSearch);
        if (offlineRows.length > 0) {
          stockDataSet(offlineRows);
          setSelectedRow(null);
          printSet(true);
        } else {
          stockDataSet([]);
          printSet(false);
        }
      } finally {
        reportLoadingSet(false);
        if (productInputRef.current) productInputRef.current.focus();
      }
      return;
    }

    const payload = resolvedProductId !== null && !forceTextSearch
      ? { productId: resolvedProductId, searchText: normalizedTypedText }
      : { searchText: normalizedTypedText };

    reportLoadingSet(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/get-product-location`,
        payload,
        { headers: { "auth-token": authInfo.token } }
      );

      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      const grouped = groupLocationRows(data);
      stockDataSet(grouped);
      setSelectedRow(null);
      printSet(true);

      const cachePayloadByProduct = new Map();
      grouped.forEach((row) => {
        const productId = row?.prod_id;
        if (productId == null) return;
        if (!cachePayloadByProduct.has(productId)) {
          cachePayloadByProduct.set(productId, []);
        }
        cachePayloadByProduct.get(productId).push(row);
      });

      await cacheSalesMasterData("product-locations", grouped);
      await Promise.all(
        Array.from(cachePayloadByProduct.entries()).map(([productId, rows]) =>
          setOfflineValue(getProductLocationCacheKey(productId), rows)
        )
      );
      await Promise.all(
        Array.from(cachePayloadByProduct.entries()).map(async ([productId, rows]) => {
          const totalQty = rows.reduce((sum, row) => sum + Number(row?.prod_qty || 0), 0);
          await setOfflineValue(getProductStockCacheKey(productId), totalQty);
        })
      );
    } catch (err) {
      try {
        const offlineRows = await buildOfflineSearchRows(normalizedTypedText, productToSearch, forceTextSearch);
        if (offlineRows.length > 0) {
          stockDataSet(offlineRows);
          setSelectedRow(null);
          printSet(true);
        } else {
          stockDataSet([]);
          printSet(false);
        }
      } catch (_fallbackError) {
        stockDataSet([]);
        printSet(false);
      }
    } finally {
      reportLoadingSet(false);
      // focus back to input
      if (productInputRef.current) productInputRef.current.focus();
    }
  }

  useEffect(() => {
    selectedProductRef.current = selectedProduct;
    searchInputRef.current = searchInput;
    getSearchResultRef.current = getSearchResult;
  }, [selectedProduct, searchInput]);

  useEffect(() => {
    const searchText = String(searchInput || "").trim();
    if (!searchText || searchText.length < 2 || selectedFromDropdownRef.current) return undefined;

    const timer = setTimeout(() => {
      searchProductOptions(searchText);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput, authInfo?.token]);

  const handleExcelExport = () => {
    if (!stockData || stockData.length === 0) return;

    const exportData = stockData.map((row, sl) => ({
      SL: sl + 1,
      Floor: pickFirstValue(row, ["prod_floor_name", "floor_name", "FloorName"], ""),
      Rack: pickFirstValue(row, ["prod_rack_name", "rack_name", "RackName"], ""),
      Category: row?.prod_cat_name ?? "",
      Brand: row?.prod_brand_name ?? "",
      Country: getProductCountry(row),
      "Product Name": row?.prod_name ?? "",
      "Current Quantity": `${format(parseFloat(pickFirstValue(row, ["prod_qty", "current_quantity", "current_stock", "stock_qty", "qty"], 0)).toFixed(2))} ${row?.prod_unit_name ?? ""}`,
      "Purchase Rate": format(parseFloat(row?.prod_purchase_rate ?? 0).toFixed(2)),
      "Average Purchase Rate": format(parseFloat(row?.average_purchase_rate ?? 0).toFixed(2)),
      "Last Sale Rate": format(parseFloat(row?.last_sale_rate ?? 0).toFixed(2)),
      "Last Sale Date": row?.last_sale_date ? row.last_sale_date.split("T")[0] : "",
    }));

    const columnWidths = [
      { wch: 5 },
      { wch: 15 },
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 18 },
      { wch: 22 },
      { wch: 18 },
      { wch: 18 },
    ];

    const name =
      selectedProduct?.prod_name ||
      selectedProduct?.product_name ||
      "Product";

    exportToExcel(exportData, `${name} storage report.xlsx`, columnWidths);
  };

  useEffect(() => {
    const handleSalesStockUpdated = (event) => {
      const updatedProductIds = Array.isArray(event?.detail?.productIds)
        ? event.detail.productIds.map((id) => String(id))
        : [];
      const activeProduct = selectedProductRef.current;
      const activeProductId = getResolvedProductId(activeProduct);
      const activeSearchText = String(
        searchInputRef.current || productInputRef.current?.value || ""
      ).trim();
      const shouldRefresh =
        activeProductId != null
          ? updatedProductIds.length === 0 || updatedProductIds.includes(String(activeProductId))
          : activeSearchText.length > 0;

      if (shouldRefresh && typeof getSearchResultRef.current === "function") {
        getSearchResultRef.current(activeProduct);
      }
    };

    window.addEventListener("sales-stock-updated", handleSalesStockUpdated);
    return () => window.removeEventListener("sales-stock-updated", handleSalesStockUpdated);
  }, []);

  const ReportDom = React.forwardRef((props, ref) => {
    const name =
      selectedProduct?.prod_name ||
      selectedProduct?.product_name ||
      "";

    return (
      <div ref={ref}>
        <InstitutionProfile />

        <div style={{ clear: "both" }} />

        {name ? (
          <Paper
            style={{
              width: "100%",
              paddingLeft: "10px",
              paddingRight: "10px",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            }}
            className="print-source"
          >
            <p style={{ width: "100%", textAlign: "center", fontWeight: 700, letterSpacing: "0.2px" }}>
              {`${name} storage report`}
            </p>
          </Paper>
        ) : null}

        {Array.isArray(stockData) && stockData.length > 0 ? (
          <Paper className={`${classes.paper} ${classes.tableCard}`} style={{ marginTop: "10px", marginBottom: "5px" }}>
            <div className={classes.tableHeaderBar}>
              <div>
                <div className={classes.tableTitle}>Search Results</div>
                <div className={classes.tableSubtitle}>{stockData.length} product{stockData.length > 1 ? "s" : ""} found</div>
              </div>
            </div>
            <TableContainer className={classes.tableContainer}>
              <Table className={`${classes.table} report-dom`} size="small">
                <TableHead>
                  <TableRow className={classes.tableHeadRow}>
                    <TableCell align="center" className={classes.headCell} style={{ width: "4%" }}>SL</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "10%" }}>Floor</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "8%" }}>Rack</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "10%" }}>Category</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "10%" }}>Brand</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "9%" }}>Country</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "12%" }}>Product Name</TableCell>
                    <TableCell align="right" className={classes.headCell} style={{ width: "9%" }}>Current Quantity</TableCell>
                    <TableCell align="right" className={classes.headCell} style={{ width: "8%" }}>Purchase Rate</TableCell>
                    <TableCell align="right" className={classes.headCell} style={{ width: "12%" }}>Purchase Average Rate</TableCell>
                    <TableCell align="right" className={classes.headCell} style={{ width: "8%" }}>Last Sale Rate</TableCell>
                    <TableCell align="center" className={classes.headCell} style={{ width: "10%" }}>Last Sale Date</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stockData.map((row, sl) => (
                    <TableRow
                      key={sl}
                      onClick={() => setSelectedRow(sl)}
                      style={{
                        backgroundColor:
                          selectedRow === sl
                            ? "#e9f7fb"
                            : sl % 2 === 0
                              ? "#ffffff"
                              : "#fbfdff",
                        cursor: "pointer",
                      }}
                    >
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{sl + 1}.</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{pickFirstValue(row, ["prod_floor_name", "floor_name", "FloorName"], "")}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{pickFirstValue(row, ["prod_rack_name", "rack_name", "RackName"], "")}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{row?.prod_cat_name ?? ""}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{row?.prod_brand_name ?? ""}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>{getProductCountry(row)}</TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {row?.prod_name ?? ""}
                      </TableCell>
                      <TableCell align="right" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {format(parseFloat(pickFirstValue(row, ["prod_qty", "current_quantity", "current_stock", "stock_qty", "qty"], 0)).toFixed(2))}{" "}
                        {row?.prod_unit_name ?? ""}
                      </TableCell>
                      <TableCell align="right" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {format(parseFloat(row?.prod_purchase_rate ?? 0).toFixed(2))}
                      </TableCell>
                      <TableCell align="right" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {format(parseFloat(row?.average_purchase_rate ?? 0).toFixed(2))}
                      </TableCell>
                      <TableCell align="right" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {format(parseFloat(row?.last_sale_rate ?? 0).toFixed(2))}
                      </TableCell>
                      <TableCell align="center" className={classes.bodyCell} style={{ whiteSpace: "nowrap" }}>
                        {row?.last_sale_date ? row.last_sale_date.split("T")[0] : ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : null}
      </div>
    );
  });

  return (
    <div className={classes.root}>
      <NetworkLoaderOverlay visible={reportLoading} message="Loading report..." />
      <Paper className={classes.paper} style={{ marginTop: "-25px", marginBottom: "5px" }}>
        <div className={classes.searchHero}>
          <div>
            <h4 className={classes.pageTitle}>Search Product</h4>
            <div className={classes.pageSubtitle}>Search by name, code, barcode, or brand</div>
          </div>
          <div className={classes.actionIcons}>
            <div className={classes.iconBtn} aria-hidden="true">PRN</div>
            <div className={classes.iconBtn} aria-hidden="true">DL</div>
          </div>
        </div>

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <Autocomplete
              size="small"
              autoHighlight={false}
              openOnFocus
              freeSolo
              style={{ width: "100%", height: "20px" }}
              options={products}
              value={selectedProduct}
              inputValue={searchInput}
              loading={productLookupLoading}
              getOptionLabel={getProductLabel}
              filterOptions={(options, state) =>
                options.filter((option) => productMatchesQuery(option, state.inputValue)).slice(0, 50)
              }
              onInputChange={(event, newInputValue, reason) => {
                setSearchInput(newInputValue || "");
                if (reason === "input") {
                  selectedFromDropdownRef.current = false;
                  selectedProductSet(null);
                }
                if (reason === "clear") {
                  selectedFromDropdownRef.current = false;
                  selectedProductSet(null);
                  stockDataSet([]);
                  printSet(false);
                  setSelectedRow(null);
                }
              }}

              onChange={(event, selectedObj, reason) => {
                selectedFromDropdownRef.current = reason === "select-option" && Boolean(selectedObj);
                selectedProductSet(selectedObj);
                setSearchInput(selectedObj ? getProductLabel(selectedObj) : "");
                stockDataSet([]);
                printSet(false);
                setSelectedRow(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                  selectedFromDropdownRef.current = false;
                  getSearchResult(null, { forceTextSearch: !selectedFromDropdownRef.current });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search by name, code, barcode, brand..."
                  placeholder="Laptop Dell | code:LP1001 | barcode:890123..."
                  variant="outlined"
                  inputRef={productInputRef}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={2} md={1}>
            <Button
              style={{ marginTop: "5px", marginLeft: "auto", fontSize: "14px", minWidth: "120px" }}
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={() => getSearchResult(null, { forceTextSearch: !selectedFromDropdownRef.current })}
              disabled={reportLoading}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {print ? (
        <Grid container>
          <Grid item xs={12}>
            <Paper style={{ borderRadius: "0px", marginTop: "-7px" }}>
              <ReactToPrint
                trigger={() => (
                  <PrintIcon
                    style={{
                      cursor: "pointer",
                      marginLeft: "30px",
                      marginTop: "3px",
                      marginBottom: "-8px",
                    }}
                  />
                )}
                content={() => reportRef.current}
              />
              <Tooltip title="Excel Download">
                <IconButton onClick={handleExcelExport}>
                  <GetAppIcon />
                </IconButton>
              </Tooltip>
            </Paper>
          </Grid>
        </Grid>
      ) : null}

      <ReportDom ref={reportRef} />
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    background: "linear-gradient(180deg, #f7fbfc 0%, #ffffff 100%)",
    minHeight: "100%",
  },
  table: {
    width: "100%",
    tableLayout: "fixed",
  },
  paper: {
    padding: theme.spacing(1.25),
    textAlign: "left",
    color: theme.palette.text.secondary,
    borderRadius: "12px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  tableCard: {
    border: "1px solid rgba(0,0,0,0.06)",
  },
  tableContainer: {
    borderRadius: "10px",
    overflowX: "auto",
  },
  tableHeaderBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "8px",
    padding: "2px 2px 10px 2px",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
  },
  tableTitle: {
    fontSize: "15px",
    fontWeight: 700,
    color: "#263238",
  },
  tableSubtitle: {
    fontSize: "12px",
    color: "#607d8b",
    marginTop: "2px",
  },
  tableHeadRow: {
    background: "linear-gradient(180deg, #f8fbfc 0%, #eef5f8 100%)",
  },
  headCell: {
    fontWeight: 700,
    fontSize: "12px",
    color: "#37474f",
    paddingTop: "9px",
    paddingBottom: "9px",
    borderBottom: "1px solid rgba(0,0,0,0.08)",
    whiteSpace: "nowrap",
  },
  bodyCell: {
    fontSize: "12.5px",
    color: "#263238",
    paddingTop: "8px",
    paddingBottom: "8px",
    borderBottom: "1px solid rgba(0,0,0,0.05)",
  },
  searchHero: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    marginTop: "-10px",
    marginBottom: "8px",
  },
  pageTitle: {
    textAlign: "left",
    margin: 0,
    padding: 0,
    fontSize: "16px",
    fontWeight: 700,
    color: "#1f2937",
  },
  pageSubtitle: {
    fontSize: "12px",
    color: "#607d8b",
    marginTop: "2px",
  },
  actionIcons: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    color: "#546e7a",
  },
  iconBtn: {
    width: "28px",
    height: "28px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f4f8fa",
    border: "1px solid rgba(0,0,0,0.06)",
    fontSize: "14px",
    cursor: "default",
    userSelect: "none",
  },
}));

const mapStateToPops = (state) => ({
  currentRoute: state.currentRouteReducer,
  authInfo: state.authInfoReducer,
});

export default connect(mapStateToPops, { currentRouteSet })(ProductSearch);
